import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Check, Wallet, Users, UserPlus, Copy, Plus, Search, PartyPopper,
} from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserAvatar, Stars, VerifiedBadge, Spinner, Pill } from "@/components/ui-bits";
import { fmtMoney } from "@/lib/constants";

let _inviteRowSeq = 0;
const mkInviteRow = () => ({ id: `inv_${++_inviteRowSeq}`, name: "", email: "" });

export default function Onboarding() {
  const { t } = useTranslation();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const STEPS = [t("hr.onboarding.step1"), t("hr.onboarding.step2"), t("hr.onboarding.step3")];
  const [step, setStep] = useState(0);
  const [company, setCompany] = useState(null);
  const [budget, setBudget] = useState("15000");
  const [experts, setExperts] = useState([]);
  const [network, setNetwork] = useState(new Set());
  const [q, setQ] = useState("");
  const [invites, setInvites] = useState(() => [mkInviteRow()]);
  const [invited, setInvited] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/company/me").then(({ data }) => {
      setCompany(data);
      if (data.budget_total) setBudget(String(data.budget_total));
      setNetwork(new Set(data.network || []));
    }).catch(() => {});
    api.get("/experts").then(({ data }) => setExperts(data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return experts.filter((e) => !s || e.name.toLowerCase().includes(s) || (e.title || "").toLowerCase().includes(s) || (e.fields || []).some((f) => f.toLowerCase().includes(s)));
  }, [experts, q]);

  const toggleExpert = async (id) => {
    const next = new Set(network);
    try {
      if (next.has(id)) { next.delete(id); await api.delete(`/company/network/${id}`); }
      else { next.add(id); await api.post("/company/network", { expert_id: id }); }
      setNetwork(next);
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveBudget = async () => {
    try { await api.patch("/company", { budget_total: Number(budget) || 0 }); } catch (err) { toast.error(apiError(err)); }
  };

  const sendInvite = async (row, idx) => {
    if (!row.name || !row.email) { toast.error(t("hr.onboarding.toastNameEmail")); return; }
    try {
      const { data } = await api.post("/company/employees/invite", row);
      setInvited((p) => [...p, data]);
      setInvites((p) => p.filter((_, i) => i !== idx).length ? p.filter((_, i) => i !== idx) : [mkInviteRow()]);
      toast.success(t("hr.onboarding.toastAdded", { name: data.name }));
    } catch (err) { toast.error(apiError(err)); }
  };

  const finish = async () => {
    setSaving(true);
    try {
      await api.patch("/company", { onboarded: true, budget_total: Number(budget) || 0 });
      await refresh();
      toast.success(t("hr.onboarding.toastReady"));
      navigate("/hr");
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const next = async () => {
    if (step === 0) await saveBudget();
    if (step < STEPS.length - 1) setStep(step + 1);
    else finish();
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="h-16 border-b border-border flex items-center px-5 sm:px-8 sticky top-0 bg-background/80 backdrop-blur-md z-10">
        <Logo to="/hr" />
        <span className="ml-3 text-sm text-muted-foreground hidden sm:block">{t("hr.onboarding.setup", { name: company?.name })}</span>
        <button onClick={finish} className="ml-auto text-sm text-muted-foreground hover:text-foreground" data-testid="skip-onboarding">{t("hr.onboarding.skip")}</button>
      </header>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        {/* Stepper */}
        <div className="flex items-center gap-2 mb-10">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-2.5">
                <span className={`grid place-items-center w-7 h-7 rounded-full text-xs font-semibold transition-colors ${i < step ? "bg-success text-success-foreground" : i === step ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
                  {i < step ? <Check size={14} strokeWidth={2.5} /> : i + 1}
                </span>
                <span className={`text-sm font-medium hidden sm:block ${i === step ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <span className="flex-1 h-px bg-border" />}
            </React.Fragment>
          ))}
        </div>

        {step === 0 && (
          <div className="animate-fade-up max-w-lg">
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 text-primary mb-5"><Wallet size={22} strokeWidth={1.75} /></span>
            <h1 className="text-2xl font-semibold tracking-tight">{t("hr.onboarding.budgetTitle")}</h1>
            <p className="text-sm text-muted-foreground mt-1.5">{t("hr.onboarding.budgetDesc")}</p>
            <div className="mt-7 space-y-2">
              <Label>{t("hr.onboarding.budgetLabel")}</Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">$</span>
                <Input data-testid="budget-input" type="number" min="0" value={budget} onChange={(e) => setBudget(e.target.value)} className="h-12 rounded-lg pl-8 text-lg font-medium tabular" />
              </div>
              <div className="flex gap-2 pt-1">
                {[10000, 25000, 50000, 100000].map((v) => (
                  <button key={v} onClick={() => setBudget(String(v))} data-testid={`budget-${v}`} className="text-xs font-medium border border-border rounded-full px-3 py-1.5 hover:border-primary/40 hover:text-primary transition-colors">{fmtMoney(v)}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="animate-fade-up">
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 text-primary mb-5"><Users size={22} strokeWidth={1.75} /></span>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">{t("hr.onboarding.networkTitle")}</h1>
                <p className="text-sm text-muted-foreground mt-1.5">{t("hr.onboarding.networkDesc")} <span className="font-medium text-foreground">{t("hr.onboarding.selected", { count: network.size })}</span>.</p>
              </div>
              <div className="relative w-full sm:w-64">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="expert-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("hr.onboarding.searchExperts")} className="h-10 rounded-lg pl-9" />
              </div>
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {filtered.map((e) => {
                const on = network.has(e.expert_id);
                return (
                  <button key={e.expert_id} onClick={() => toggleExpert(e.expert_id)} data-testid={`pick-expert-${e.expert_id}`}
                    className={`text-left card-milled rounded-[0.8rem] p-4 flex items-start gap-3 transition-colors ${on ? "ring-2 ring-primary border-primary" : "hover:border-primary/40"}`}>
                    <UserAvatar src={fileUrl(e.picture)} name={e.name} size={44} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium text-sm truncate">{e.name}</p>
                        {e.verified && <VerifiedBadge label="" />}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{e.title}</p>
                      <div className="mt-1.5 flex items-center gap-2">
                        <Stars value={e.rating_avg} size={12} />
                        {(e.fields || [])[0] && <Pill className="bg-primary/10 text-primary">{e.fields[0]}</Pill>}
                      </div>
                    </div>
                    <span className={`grid place-items-center w-6 h-6 rounded-full shrink-0 border ${on ? "bg-primary border-primary text-primary-foreground" : "border-border text-transparent"}`}>
                      <Check size={14} strokeWidth={3} />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-up max-w-2xl">
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 text-primary mb-5"><UserPlus size={22} strokeWidth={1.75} /></span>
            <h1 className="text-2xl font-semibold tracking-tight">{t("hr.onboarding.inviteTitle")}</h1>
            <p className="text-sm text-muted-foreground mt-1.5">{t("hr.onboarding.inviteDesc")}</p>

            <div className="mt-6 space-y-3">
              {invites.map((row, idx) => (
                <div key={row.id} className="flex gap-2 items-center">
                  <Input data-testid={`invite-name-${idx}`} placeholder={t("hr.onboarding.namePlaceholder")} value={row.name} onChange={(e) => setInvites((p) => p.map((r, i) => i === idx ? { ...r, name: e.target.value } : r))} className="h-11 rounded-lg" />
                  <Input data-testid={`invite-email-${idx}`} type="email" placeholder={t("hr.onboarding.emailPlaceholder")} value={row.email} onChange={(e) => setInvites((p) => p.map((r, i) => i === idx ? { ...r, email: e.target.value } : r))} className="h-11 rounded-lg" />
                  <Button data-testid={`invite-send-${idx}`} onClick={() => sendInvite(row, idx)} className="h-11 rounded-lg shrink-0">{t("common.add")}</Button>
                </div>
              ))}
              <button onClick={() => setInvites((p) => [...p, mkInviteRow()])} data-testid="add-invite-row" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:gap-2 transition-all">
                <Plus size={15} /> {t("hr.onboarding.addAnother")}
              </button>
            </div>

            {invited.length > 0 && (
              <div className="mt-7 card-milled rounded-[0.8rem] p-5">
                <p className="text-sm font-medium mb-3">{t("hr.onboarding.invitedShare", { count: invited.length })}</p>
                <div className="space-y-2">
                  {invited.map((u) => (
                    <div key={u.email} className="flex items-center gap-3 text-sm bg-secondary/60 rounded-lg px-3 py-2">
                      <span className="font-medium">{u.name}</span>
                      <span className="text-muted-foreground">{u.email}</span>
                      <span className="ml-auto font-mono text-xs bg-card border border-border rounded px-2 py-1">{u.temp_password}</span>
                      <button onClick={() => { navigator.clipboard.writeText(`${u.email} / ${u.temp_password}`); toast.success(t("common.copied")); }} className="text-muted-foreground hover:text-primary"><Copy size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Nav */}
        <div className="mt-10 flex items-center justify-between border-t border-border pt-6">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="onb-back" className="rounded-lg gap-1.5">
            <ArrowLeft size={16} /> {t("common.back")}
          </Button>
          <Button onClick={next} disabled={saving} data-testid="onb-next" className="rounded-lg gap-1.5 active:scale-[0.98] transition-transform">
            {saving ? <Spinner /> : step === STEPS.length - 1 ? <>{t("hr.onboarding.finishSetup")} <PartyPopper size={16} /></> : <>{t("common.continue")} <ArrowRight size={16} /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}
